"use client"

/**
 * Shell-global greeting banner background — brand tint or a picked illustration.
 *
 * Preference is stored **per workspace identity** (student, faculty/member,
 * administrator). Switching the identity pill restores that role's background.
 */

import * as React from "react"

import {
  getStorageItem,
  setStorageItem,
  subscribeToStorageKey,
} from "@exxatdesignux/ui/lib/persisted-state"

import {
  getLoginSession,
  subscribeLoginSession,
  type WorkspaceRole,
} from "@/lib/login-session"
import {
  GREETING_BANNER_DEFAULT_BY_ROLE,
  isGreetingBannerIllustrationId,
  resolveGreetingBannerIllustrationForRole,
  type GreetingBannerIllustrationId,
} from "@/lib/greeting-banner-illustrations"

const STORAGE_KEY = "greeting-banner-illustration"
const VERSION = 2

type RoleIllustrationMap = Partial<Record<WorkspaceRole, GreetingBannerIllustrationId>>

const listeners = new Set<() => void>()
let snapshot: RoleIllustrationMap | null = null
let hydrated = false

function sanitizeRoleMap(raw: Record<string, unknown>): RoleIllustrationMap {
  const map: RoleIllustrationMap = {}
  for (const role of Object.keys(GREETING_BANNER_DEFAULT_BY_ROLE) as WorkspaceRole[]) {
    const value = raw[role]
    if (isGreetingBannerIllustrationId(value)) {
      map[role] = value
    }
  }
  return map
}

function parseStored(raw: string | null): RoleIllustrationMap {
  if (!raw) return {}

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return {}

    const version = (parsed as { v?: number }).v
    if (version === VERSION) {
      const data = (parsed as { d?: unknown }).d
      if (typeof data === "object" && data !== null) {
        return sanitizeRoleMap(data as Record<string, unknown>)
      }
      return {}
    }

    if (
      version === 1 &&
      isGreetingBannerIllustrationId((parsed as { d?: unknown }).d)
    ) {
      const legacy = (parsed as { d: GreetingBannerIllustrationId }).d
      return {
        student: legacy,
        member: legacy,
        administrator: legacy,
      }
    }
  } catch {
    // Unparseable payload — fall through to empty map.
  }

  return {}
}

function subscribe(onChange: () => void) {
  listeners.add(onChange)
  const unsubscribeStorage = subscribeToStorageKey(STORAGE_KEY, () => {
    hydrated = false
    onChange()
  })
  const unsubscribeSession = subscribeLoginSession(onChange)
  return () => {
    listeners.delete(onChange)
    unsubscribeStorage()
    unsubscribeSession()
  }
}

function readMap(): RoleIllustrationMap {
  if (hydrated && snapshot) return snapshot
  hydrated = true
  snapshot = parseStored(getStorageItem(STORAGE_KEY))
  return snapshot
}

function readForActiveRole(): GreetingBannerIllustrationId {
  return resolveGreetingBannerIllustrationForRole(getLoginSession().role, readMap())
}

export function setGreetingBannerIllustration(next: GreetingBannerIllustrationId) {
  const role = getLoginSession().role
  const map = { ...readMap(), [role]: next }
  snapshot = map
  hydrated = true
  setStorageItem(STORAGE_KEY, JSON.stringify({ v: VERSION, d: map }))
  for (const listener of listeners) listener()
}

export function useGreetingBannerIllustration(): GreetingBannerIllustrationId {
  return React.useSyncExternalStore(
    subscribe,
    readForActiveRole,
    () => GREETING_BANNER_DEFAULT_BY_ROLE.student,
  )
}
